// Structured view of the agent's markdown answer. Surfaces named sections
// (win condition, key interactions, mana base, sideboard guide, honorable
// mentions, exemplars consulted) as styled cards. The raw answer is still
// available verbatim in a <details> on the parent page for copy purposes.
//
// Inline text rendering lives in the shared Markdown component — see
// ./Markdown.tsx for the supported syntax ({Card}, **bold**, bullets, urls).

import { Fragment, type ReactNode } from "react";
import {
  BookOpen,
  Layers,
  Mountain,
  Scale,
  ShieldCheck,
  Sparkles,
  Star,
  Swords,
  Target,
  Users,
} from "lucide-react";
import Markdown from "./Markdown";
import { cn } from "../lib/cn";
import type { ParsedSections } from "../lib/answer-sections";

interface Props {
  parsed: ParsedSections;
  /** Hide the "Exemplars Consulted" section — useful when the parent page
   *  already renders the structured exemplar list from the retrieval layer
   *  (which has real source URLs, not just what the agent echoed). */
  hideExemplars?: boolean;
  /** Optional content rendered inside the Mana Base panel ABOVE the
   *  prose body. Used by DeckDetail to inject the live mana-curve
   *  chart computed from the actual deck cards (which AnswerSections
   *  itself doesn't have access to). */
  manaBaseExtras?: ReactNode;
}

interface PanelDef {
  key: keyof ParsedSections | "other";
  title: string;
  icon: ReactNode;
  body?: string;
  accent?: "accent" | "warn" | "muted";
  /** Span both grid columns. Used for sections that read better as a
   *  full-width row (e.g. Weaknesses) so the grid doesn't strand half
   *  a row in whitespace. */
  fullWidth?: boolean;
}

const iconProps = { size: 14, strokeWidth: 1.75 } as const;

// Among the 3 ways to pair 4 panels into 2 rows of 2, pick the pairing
// that minimizes the worst per-row body-length delta. Body length (chars)
// is a cheap proxy for rendered card height — close enough that the
// chosen pairing is the one where adjacent cards in a row have the most
// similar height, which keeps the equal-height grid from over-stretching
// the shorter card. Identity is preserved (no card is dropped or
// duplicated); only row membership and within-row order changes.
function pairByLength(items: PanelDef[]): PanelDef[] {
  if (items.length !== 4) return items;
  const len = (p: PanelDef) => (p.body ?? "").length;
  const [a, b, c, d] = items as [PanelDef, PanelDef, PanelDef, PanelDef];
  const pairings: Array<[PanelDef, PanelDef, PanelDef, PanelDef]> = [
    [a, b, c, d],
    [a, c, b, d],
    [a, d, b, c],
  ];
  let best = pairings[0]!;
  let bestScore = Infinity;
  for (const p of pairings) {
    const score = Math.max(
      Math.abs(len(p[0]) - len(p[1])),
      Math.abs(len(p[2]) - len(p[3])),
    );
    if (score < bestScore) {
      bestScore = score;
      best = p;
    }
  }
  // Within each row, render the accent card on the left when present —
  // keeps eye-catching panels in the dominant column.
  const orderRow = (x: PanelDef, y: PanelDef): [PanelDef, PanelDef] =>
    !x.accent && y.accent ? [y, x] : [x, y];
  const [r1a, r1b] = orderRow(best[0], best[1]);
  const [r2a, r2b] = orderRow(best[2], best[3]);
  return [r1a, r1b, r2a, r2b];
}

export default function AnswerSections({ parsed, hideExemplars, manaBaseExtras }: Props) {
  // Layout intent:
  //   rows 1-2: the four core analysis cards (Win, Weaknesses, Key, Side),
  //             paired by closest body length so adjacent equal-height
  //             cards don't strand whitespace next to a long neighbor
  //   row 3:    Mana Base (full-width)
  //   row 4:    Strategy | Meta
  const corePanels: PanelDef[] = [
    { key: "winCondition", title: "Win Condition", icon: <Target {...iconProps} />, body: parsed.winCondition, accent: "accent" },
    { key: "weaknesses", title: "Weaknesses", icon: <ShieldCheck {...iconProps} />, body: parsed.weaknesses, accent: "warn" },
    { key: "keyInteractions", title: "Key Interactions", icon: <Sparkles {...iconProps} />, body: parsed.keyInteractions, accent: "accent" },
    { key: "sideboardGuide", title: "Sideboard Guide", icon: <Swords {...iconProps} />, body: parsed.sideboardGuide },
  ].filter((p) => p.body) as PanelDef[];
  const trailingPanels: PanelDef[] = [
    { key: "manaBase", title: "Mana Base", icon: <Mountain {...iconProps} />, body: parsed.manaBase, fullWidth: true },
    { key: "strategy", title: "Strategy", icon: <BookOpen {...iconProps} />, body: parsed.strategy, accent: "accent" },
    { key: "meta", title: "Meta", icon: <Scale {...iconProps} />, body: parsed.meta },
  ].filter((p) => p.body) as PanelDef[];
  const primary: PanelDef[] = [...pairByLength(corePanels), ...trailingPanels];

  return (
    <div className="space-y-5">
      {primary.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {primary.map((p) => (
            <div
              key={p.key as string}
              className={cn("h-full", p.fullWidth && "md:col-span-2")}
            >
              <Panel title={p.title} icon={p.icon} accent={p.accent}>
                {p.key === "manaBase" && manaBaseExtras ? (
                  <Fragment>
                    <Markdown text={p.body!} />
                    <div className="pt-3 mt-3 border-t border-border/50">
                      {manaBaseExtras}
                    </div>
                  </Fragment>
                ) : (
                  <Markdown text={p.body!} />
                )}
              </Panel>
            </div>
          ))}
        </div>
      )}

      {parsed.honorableMentions && (
        <Panel title="Honorable Mentions" icon={<Star {...iconProps} />}>
          <Markdown text={parsed.honorableMentions} />
        </Panel>
      )}

      {parsed.exemplarsConsulted && !hideExemplars && (
        <Panel title="Exemplars Consulted" icon={<Users {...iconProps} />} accent="muted">
          <Markdown text={parsed.exemplarsConsulted} />
        </Panel>
      )}

      {parsed.other.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {parsed.other.map((o, i) => (
            <Panel key={i} title={o.title} icon={<Layers {...iconProps} />} accent="muted">
              <Markdown text={o.body} />
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
  accent,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  accent?: "accent" | "warn" | "muted";
}) {
  return (
    <section className="surface p-4 h-full flex flex-col">
      <header className={cn(
        "flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium mb-2",
        accent === "accent" && "text-accent",
        accent === "warn" && "text-warn",
        (!accent || accent === "muted") && "text-fg-subtle",
      )}>
        {icon}
        <span>{title}</span>
      </header>
      <div className="text-sm leading-relaxed text-fg space-y-2 flex-1">
        {children}
      </div>
    </section>
  );
}

